import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import {
  INSTRUMENT_VERSION,
  INTRO_EYEBROW,
  INTRO_TITLE,
  INTRO_PARAGRAPHS,
  INTRO_BOUNDARY,
  RESPONDENT_INSTRUCTION_TITLE,
  RESPONDENT_INSTRUCTION,
  QUESTIONS,
  RESPONSE_SCALE,
  UNAVAILABLE_OPTIONS,
  WRITTEN_EXAMPLE,
  DIMENSIONS,
  BAND_LABELS,
  FRICTION_BAND_LABELS,
  FRICTION_NARRATIVES,
  MISMATCH_INTRO,
  CATEGORIES,
  PRIMARY_SIGNAL_NONE,
  STRONGEST_TIE,
  OTHER_POSSIBLE_CAUSES,
  LIMITATIONS,
  CTA_PRIMARY,
  CTA_SECONDARY,
  CTA_TEXT_LINK,
} from "../data/review";
import { computeProfile, validateSubmission } from "../lib/scoring";
import {
  STORAGE_KEY,
  canCalculate,
  getNextStep,
  loadReviewSession,
} from "../lib/reviewState";
import { OperationalClarityResultEmail } from "../components/OperationalClarityResultEmail";

const DIM_MAP = Object.fromEntries(DIMENSIONS.map((d) => [d.key, d]));

const BAND_MARKERS = {
  "holding-up": "bg-ink",
  "mostly-working": "bg-mute",
  "breaking-down": "bg-terra",
  "getting-in-the-way": "bg-terra",
  insufficient: "bg-stone",
};

const inputCls =
  "w-full bg-transparent border-b border-ink/25 focus:border-terra outline-none py-3 text-base placeholder:text-mute/50 transition-colors duration-300";

const ScaleOption = ({ name, testId, checked, onSelect, label }) => (
  <label className="block cursor-pointer">
    <input
      type="radio"
      name={name}
      checked={checked}
      onChange={onSelect}
      data-testid={testId}
      className="peer sr-only"
    />
    <span
      className={`block px-4 py-3 border text-sm transition-colors duration-200 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-terra ${
        checked ? "bg-terra border-terra text-white" : "border-ink/20 text-ink/80 hover:border-ink"
      }`}
    >
      {label}
    </span>
  </label>
);

export default function OperationalClarityReview() {
  const [step, setStep] = useState("context"); // "context" | question index | "review" | "results"
  const [area, setArea] = useState("");
  const [concern, setConcern] = useState("");
  const [writtenExample, setWrittenExample] = useState("");
  const [answers, setAnswers] = useState({});
  const [profile, setProfile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [resetNotice, setResetNotice] = useState(false);

  useEffect(() => {
    const { session: saved, resetRequired } = loadReviewSession(sessionStorage);
    setResetNotice(resetRequired);
    if (saved) {
      setArea(saved.area || "");
      setConcern(saved.concern || "");
      setWrittenExample(saved.writtenExample || "");
      setAnswers(saved.answers || {});
      const isRestorableStep =
        saved.step === "context" ||
        saved.step === "review" ||
        (Number.isInteger(saved.step) && saved.step >= 0 && saved.step < QUESTIONS.length);
      if (isRestorableStep) {
        setStep(saved.step);
      }
    }
  }, []);

  useEffect(() => {
    if (step === "results") return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          instrumentVersion: INSTRUMENT_VERSION,
          area,
          concern,
          writtenExample,
          answers,
          step,
        })
      );
    } catch {
      /* session storage unavailable */
    }
  }, [area, concern, writtenExample, answers, step]);

  const isQuestion = typeof step === "number";
  const question = isQuestion ? QUESTIONS[step] : null;
  const scale = question ? RESPONSE_SCALE : [];
  const answeredCount = QUESTIONS.filter((q) => answers[q.id] !== undefined).length;

  const setAnswer = (id, value) => setAnswers((a) => ({ ...a, [id]: value }));

  const goTo = (s) => {
    setStep(s);
    window.scrollTo(0, 0);
  };

  const calculate = () => {
    const payload = {
      instrumentVersion: INSTRUMENT_VERSION,
      areaAssessed: area.trim(),
      recurringConcern: concern.trim() || undefined,
      writtenExample: writtenExample.trim() || undefined,
      answers: QUESTIONS.map((q) => ({ questionId: q.id, value: answers[q.id] })),
    };
    const errs = validateSubmission(payload);
    setErrors(errs);
    if (errs.length) return;
    setProfile(computeProfile(payload));
    goTo("results");
  };

  const reset = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setArea("");
    setConcern("");
    setWrittenExample("");
    setAnswers({});
    setProfile(null);
    setErrors([]);
    setResetNotice(false);
    goTo("context");
  };

  const answerLabel = (q) => {
    const v = answers[q.id];
    if (v === undefined) return "—";
    const s = [...RESPONSE_SCALE, ...UNAVAILABLE_OPTIONS].find((o) => o.value === v);
    return s ? s.label : "—";
  };

  /* ---------------- RESULTS ---------------- */
  if (step === "results" && profile) {
    const eligible = profile.dimensions.filter((d) => d.band !== "insufficient");
    const asc = [...eligible].sort((a, b) => a.index - b.index);
    const questionDims = asc.slice(0, 2).map((dimension) => dimension.dimension);
    const visibleMismatches = profile.mismatches.filter(
      (mismatch) => mismatch.level === "possible" || mismatch.level === "strong"
    );

    return (
      <main data-testid="review-results-page" className="ocr bg-paper">
        <div className="max-w-3xl mx-auto px-6 md:px-12 pt-40 md:pt-52 pb-24">
          <h1 data-testid="result-heading" className="font-display text-4xl md:text-5xl tracking-tight">
            Your Operational Clarity Profile.
          </h1>

          <div className="mt-12 border-l-2 border-terra pl-6">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Overall result</p>
            <h2 data-testid="result-category" className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
              {CATEGORIES[profile.overallCategory].label}
            </h2>
            <p data-testid="result-interpretation" className="mt-4 text-base leading-relaxed text-mute">
              {CATEGORIES[profile.overallCategory].summary}
            </p>
            {profile.availability.unscoredCount > 0 && (
              <p data-testid="result-availability" className="mt-3 text-sm leading-relaxed text-mute">
                {profile.availability.occurrenceUnavailable} answer{profile.availability.occurrenceUnavailable === 1 ? "" : "s"} marked the situation as not having come up; {profile.availability.visibilityUnavailable} answer{profile.availability.visibilityUnavailable === 1 ? "" : "s"} marked that there was not enough visibility to answer.
              </p>
            )}
          </div>

          <p data-testid="result-area" className="mt-10 text-base text-ink">
            <span className="text-[12px] font-semibold uppercase tracking-[0.18em] text-mute block">Area reviewed</span>
            <span className="mt-1 block font-medium">{profile.areaAssessed}</span>
            {profile.recurringConcern && (
              <span className="mt-2 block text-sm text-mute">What keeps going wrong: {profile.recurringConcern}</span>
            )}
          </p>

          <div data-testid="result-friction" className="mt-12 border-t border-rule pt-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Day-to-day friction</p>
            <p className="mt-3 font-display text-2xl tracking-tight">{FRICTION_BAND_LABELS[profile.friction.band]}</p>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-mute">{FRICTION_NARRATIVES[profile.friction.band]}</p>
            {profile.friction.limitedEvidence && (
              <p className="mt-2 text-sm font-medium text-ink">Based on limited recent evidence.</p>
            )}
          </div>

          <div data-testid="result-dimensions" className="mt-12 border-t border-rule pt-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Five-area Operational Clarity Profile</p>
            {profile.dimensions.map((d) => {
              const dim = DIM_MAP[d.dimension];
              return (
                <div key={d.dimension} data-testid={`result-dim-${d.dimension}`} className="py-5 border-b border-rule">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-sans text-base font-semibold tracking-tight">{dim.name}</h3>
                    <span className="flex items-center gap-2 text-right">
                      <span aria-hidden="true" className={`inline-block h-2 w-2 shrink-0 ${BAND_MARKERS[d.band]}`} />
                      <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-ink/80">
                        {BAND_LABELS[d.band]}
                      </span>
                    </span>
                  </div>
                  {d.band !== "insufficient" && (
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-mute">{dim.narratives[d.band]}</p>
                  )}
                </div>
              );
            })}
          </div>

          {visibleMismatches.length > 0 && (
            <div data-testid="result-mismatches" className="mt-12 border-t border-rule pt-8">
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">
                Worth checking
              </p>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mute">{MISMATCH_INTRO}</p>
              <div className="mt-5 space-y-5">
                {visibleMismatches.map((mismatch) => (
                  <div key={mismatch.area} data-testid={`mismatch-${mismatch.area}`}>
                    <p className="text-sm font-semibold text-ink">
                      {mismatch.area === "friction" ? "Day-to-day friction" : DIM_MAP[mismatch.area].name}
                    </p>
                    <p className="mt-1 max-w-2xl text-sm leading-relaxed text-mute">{mismatch.message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-rule pt-8">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Where to look first</p>
              <p data-testid="result-primary" className="mt-3 font-display text-xl tracking-tight">
                {profile.primarySignal
                  ? DIM_MAP[profile.primarySignal].name
                  : PRIMARY_SIGNAL_NONE}
              </p>
            </div>
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">What appears to be holding up best</p>
              <p data-testid="result-strongest" className="mt-3 font-display text-xl tracking-tight">
                {profile.strongestCondition
                  ? DIM_MAP[profile.strongestCondition].name
                  : STRONGEST_TIE}
              </p>
            </div>
          </div>

          <div className="mt-12 bg-ink text-paper px-6 md:px-10 py-10">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-terra">
              What to check next
            </p>
            {questionDims.length ? (
              <ol data-testid="result-questions" className="mt-5 space-y-4">
                {questionDims.map((k, i) => (
                  <li key={k} className="flex gap-4">
                    <span className="text-xs font-semibold tracking-[0.18em] text-terra pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-display text-lg leading-snug tracking-tight">{DIM_MAP[k].question}</p>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-5 text-sm text-paper/60">Insufficient visibility to identify leadership questions.</p>
            )}
            {profile.recommendedActions.length > 0 && (
              <div className="mt-8 border-t border-paper/15 pt-6">
                <ol data-testid="result-actions" className="mt-4 space-y-3">
                  {profile.recommendedActions.map((a, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="text-xs font-semibold tracking-[0.18em] text-terra pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <p className="text-sm leading-relaxed text-paper/80">{a}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          <div data-testid="result-alternatives" className="mt-12 border-t border-rule pt-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Other possible causes</p>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-mute">{OTHER_POSSIBLE_CAUSES}</p>
          </div>

          <div data-testid="result-limitations" className="mt-10 border-t border-rule pt-8">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">What this result does not prove</p>
            {LIMITATIONS.map((t, i) => (
              <p key={i} className="mt-3 max-w-2xl text-sm leading-relaxed text-mute">{t}</p>
            ))}
          </div>

          {profile.writtenExample && (
            <div data-testid="result-example" className="mt-10 border-t border-rule pt-8">
              <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-mute">Your example</p>
              <p className="mt-3 max-w-2xl whitespace-pre-wrap text-sm leading-relaxed text-ink">{profile.writtenExample}</p>
            </div>
          )}

          <OperationalClarityResultEmail key={profile.areaAssessed} profile={profile} />

          <div className="mt-12 pt-2">
            <Link
              to={CTA_PRIMARY.to}
              data-testid="cta-talk"
              className="inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-ink transition-colors duration-300"
            >
              {CTA_PRIMARY.label} <ArrowRight size={14} />
            </Link>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-mute">{CTA_PRIMARY.supporting}</p>
            <div className="mt-8 flex flex-wrap items-center gap-6">
              <button
                onClick={reset}
                data-testid="cta-review-another"
                className="inline-flex items-center gap-2 border border-ink/25 text-ink text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:border-ink hover:bg-ink hover:text-paper transition-colors duration-300"
              >
                {CTA_SECONDARY.label}
              </button>
              <Link
                to={CTA_TEXT_LINK.to}
                data-testid="cta-how-bos-works"
                className="text-[12px] font-semibold uppercase tracking-[0.12em] text-terra link-rule"
              >
                {CTA_TEXT_LINK.label}
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  /* ---------------- CONTEXT / QUESTIONS / REVIEW ---------------- */
  return (
    <main data-testid="review-page" className="ocr bg-paper">
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-36 md:pt-48 pb-24 md:pb-32">
        {step === "context" && (
          <div data-testid="context-screen" className="max-w-4xl">
            <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-terra">
              {INTRO_EYEBROW}
            </p>
            <h1 className="mt-6 max-w-4xl font-display text-[2.75rem] leading-[1.04] tracking-tight sm:text-5xl md:text-[4.25rem]">{INTRO_TITLE}</h1>
            <p className="mt-5 text-[12px] font-semibold uppercase tracking-[0.18em] text-mute">
              Free · {QUESTIONS.length} questions · About five minutes
            </p>
            {resetNotice && (
              <p data-testid="session-reset-notice" className="mt-5 border-l-2 border-terra pl-5 text-sm leading-relaxed text-ink">
                Your earlier review used a different instrument version, so it has been reset. Please begin this review again.
              </p>
            )}
            <div className="mt-12 max-w-3xl space-y-6">
              {INTRO_PARAGRAPHS.map((p, i) => (
                <p key={i} className="text-base leading-[1.75] text-mute md:text-[1.05rem]">{p}</p>
              ))}
            </div>
            <div className="mt-10 grid max-w-4xl gap-7 md:grid-cols-2 md:gap-10">
              <p className="border-l-2 border-terra pl-5 text-sm leading-relaxed text-ink">{INTRO_BOUNDARY}</p>
              <div className="border-l-2 border-terra pl-5">
                <p className="text-sm font-semibold text-ink">{RESPONDENT_INSTRUCTION_TITLE}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink">{RESPONDENT_INSTRUCTION}</p>
              </div>
            </div>
            <div className="mt-14 max-w-4xl space-y-9 border-t border-rule pt-10">
              <div>
                <label className="text-[12px] font-semibold uppercase tracking-[0.18em] text-mute" htmlFor="review-area">
                  What regular piece of work are you reviewing? *
                </label>
                <p className="mt-2 text-sm leading-relaxed text-mute">Choose one team activity, workflow, responsibility or recurring type of work.</p>
                <input
                  id="review-area"
                  data-testid="review-area"
                  className={inputCls}
                  maxLength={120}
                  placeholder="e.g. customer complaints, job approvals, shift handovers"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                />
              </div>
              <div>
                <label className="text-[12px] font-semibold uppercase tracking-[0.18em] text-mute" htmlFor="review-concern">
                  What keeps going wrong, slowing down or coming back to managers? (optional)
                </label>
                <p className="mt-2 text-sm leading-relaxed text-mute">A short description is enough. You do not need to diagnose the cause.</p>
                <textarea
                  id="review-concern"
                  data-testid="review-concern"
                  rows={2}
                  maxLength={300}
                  className={`${inputCls} resize-none`}
                  placeholder="e.g. Routine decisions keep being escalated to the team leader."
                  value={concern}
                  onChange={(e) => setConcern(e.target.value)}
                />
              </div>
            </div>
            <button
              data-testid="review-begin"
              disabled={!area.trim()}
              onClick={() => goTo(0)}
              className="mt-10 inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-ink transition-colors duration-300 disabled:opacity-40"
            >
              Begin the review <ArrowRight size={14} />
            </button>
            <p className="mt-6 max-w-3xl text-xs leading-relaxed text-mute">
              No personal information is required to answer or view results. Answers are retained only
              during this browser session.
            </p>
          </div>
        )}

        {isQuestion && question && (
          <div data-testid={`question-screen-${question.id.toLowerCase()}`}>
            <div className="flex items-baseline justify-between">
              <p data-testid="question-progress" className="text-[12px] font-semibold uppercase tracking-[0.2em] text-terra">
                Question {question.n} of {QUESTIONS.length}
              </p>
            </div>
            <div className="mt-4 h-1 w-full bg-ink/10" aria-hidden="true">
              <div
                data-testid="question-progress-bar"
                className="h-full bg-terra transition-[width] duration-300"
                style={{ width: `${((step + 1) / QUESTIONS.length) * 100}%` }}
              />
            </div>
            <fieldset className="mt-8">
              <legend className="font-display text-2xl md:text-[1.7rem] leading-snug tracking-tight">
                {question.text}
              </legend>
              <p className="mt-2 text-xs text-mute/70">
                Answer with <span className="font-medium text-ink">{area}</span> in mind.
              </p>
              <div className="mt-6 space-y-2">
                {scale.map((s) => (
                  <ScaleOption
                    key={s.value}
                    name={question.id}
                    testId={`answer-${question.id.toLowerCase()}-${s.value}`}
                    checked={answers[question.id] === s.value}
                    onSelect={() => setAnswer(question.id, s.value)}
                    label={s.label}
                  />
                ))}
                {UNAVAILABLE_OPTIONS.map((option) => (
                  <ScaleOption
                    key={option.value}
                    name={question.id}
                    testId={`answer-${question.id.toLowerCase()}-${option.value.toLowerCase()}`}
                    checked={answers[question.id] === option.value}
                    onSelect={() => setAnswer(question.id, option.value)}
                    label={option.label}
                  />
                ))}
              </div>
            </fieldset>
            <div className="mt-10 flex items-center justify-between">
              <button
                data-testid="question-back"
                onClick={() => goTo(step === 0 ? "context" : step - 1)}
                className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-mute hover:text-ink transition-colors duration-300"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                data-testid="question-next"
                disabled={answers[question.id] === undefined}
                onClick={() => goTo(getNextStep(step))}
                className="inline-flex items-center gap-2 bg-ink text-paper text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-terra transition-colors duration-300 disabled:opacity-40"
              >
                {step === QUESTIONS.length - 1 ? "Review my answers" : "Next"} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {step === "review" && (
          <div data-testid="review-screen">
            <p className="text-[12px] font-semibold uppercase tracking-[0.22em] text-terra">
              Review before calculation · {answeredCount} of {QUESTIONS.length} answered
            </p>
            <h1 className="mt-4 font-display text-4xl tracking-tight">Check your answers</h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-mute">
              Make sure your answers reflect what actually happened during the past eight weeks, not how the work is meant to run.
            </p>
            <div className="mt-8 border-y border-rule py-6">
              <label className="text-[12px] font-semibold uppercase tracking-[0.18em] text-mute" htmlFor="review-example">
                {WRITTEN_EXAMPLE.label}
              </label>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-mute">{WRITTEN_EXAMPLE.prompt}</p>
              <textarea
                id="review-example"
                data-testid="review-example"
                rows={3}
                maxLength={WRITTEN_EXAMPLE.maxLength}
                className={`${inputCls} mt-3 resize-none`}
                value={writtenExample}
                onChange={(event) => setWrittenExample(event.target.value)}
              />
            </div>
            <div className="mt-8">
              {QUESTIONS.map((q, i) => (
                <div key={q.id} data-testid={`review-row-${q.id.toLowerCase()}`} className="py-4 border-b border-rule grid grid-cols-12 gap-3 items-start">
                  <span className="col-span-1 text-[12px] font-semibold tracking-[0.18em] text-terra pt-0.5">
                    {String(q.n).padStart(2, "0")}
                  </span>
                  <p className="col-span-11 md:col-span-7 text-sm leading-relaxed text-ink/85">{q.text}</p>
                  <p className="col-span-9 md:col-span-3 text-sm font-medium text-ink">{answerLabel(q)}</p>
                  <button
                    data-testid={`review-change-${q.id.toLowerCase()}`}
                    onClick={() => goTo(i)}
                    className="col-span-3 md:col-span-1 text-right text-[12px] font-semibold uppercase tracking-[0.12em] text-terra link-rule"
                  >
                    Change
                  </button>
                </div>
              ))}
            </div>
            {errors.length > 0 && (
              <p data-testid="review-errors" className="mt-4 text-sm text-terra">
                Some answers need attention before your result can be calculated.
              </p>
            )}
            <div className="mt-10 flex items-center justify-between">
              <button
                data-testid="review-back"
                onClick={() => goTo(QUESTIONS.length - 1)}
                className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-mute hover:text-ink transition-colors duration-300"
              >
                <ArrowLeft size={14} /> Edit answers
              </button>
              <button
                data-testid="review-calculate"
                disabled={!canCalculate(answers)}
                onClick={calculate}
                className="inline-flex items-center gap-2 bg-terra text-white text-[12px] font-semibold uppercase tracking-[0.12em] px-6 py-3.5 hover:bg-ink transition-colors duration-300 disabled:opacity-40"
              >
                See my result <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
